import { Link } from "@tanstack/react-router";
import { formatCurrencyFromCentsToReais } from "@repo/lib";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { mapPaymentsToHomeData } from "@/lib/mappers/payments-home";
import {
  CardGroupItem,
  CardGroupLabel,
  CardGroupRoot,
  CardGroupValue,
} from "@/components/card-group";
import { HomeSectionLivancePayEmptyState } from "@/components/home/home-section-livance-pay-empty-state";

export const HomeSectionLivancePayCardGroupFragment = graphql(`
  fragment HomeSectionLivancePayCardGroupFragment on query_root {
    pagosOnline: expensum_online_payment_aggregate(
      where: {
        createdAt: { _gte: $dateStartCount, _lt: $dateEndCount }
        status: { _eq: "paid" }
        codUsuario: { _eq: $codUsuario }
      }
    ) {
      aggregate {
        count(distinct: true)
      }
    }
    pendentesOnline: expensum_online_payment_aggregate(
      where: {
        createdAt: { _gte: $dateStartCount, _lt: $dateEndCount }
        status: { _eq: "pending" }
        codUsuario: { _eq: $codUsuario }
      }
    ) {
      aggregate {
        count(distinct: true)
      }
    }
    pagosTotem: expensum_appointment_paid_aggregate(
      where: {
        createdAt: { _gte: $dateStartCount, _lt: $dateEndCount }
        codUsuario: { _eq: $codUsuario }
      }
    ) {
      aggregate {
        count(distinct: true)
      }
    }
    pendentesTotem: expensum_event_totem_payment_sent_aggregate(
      where: {
        createdAt: { _gte: $dateStartCount, _lt: $dateEndCount }
        codUsuario: { _eq: $codUsuario }
      }
    ) {
      aggregate {
        count(distinct: true)
      }
    }
    ExpensumGetPaymentsChartsInfo(
      arg1: {
        intervalEnd: $dateEndAction
        intervalStart: $dateStartAction
        periodicity: "monthly"
      }
    ) {
      periods {
        intervalEnd
        intervalStart
        totalAmount
        onlinePayments {
          created {
            totalAmount
          }
          paid {
            totalAmount
          }
        }
        totemPayments {
          created {
            totalAmount
          }
          paid {
            totalAmount
          }
        }
      }
    }
    expensumRecipient: ExpensumGetRecipient(
      arg1: { codUsuario: $codUsuario, codClinica: $codClinica }
    ) {
      recipient {
        pagarmeId
      }
    }
  }
`);

interface HomeSectionLivancePayCardGroupProps {
  data: FragmentType<typeof HomeSectionLivancePayCardGroupFragment>;
  periodDetails: string;
}

export const HomeSectionLivancePayCardGroup = ({
  data,
  periodDetails,
}: HomeSectionLivancePayCardGroupProps): JSX.Element => {
  const fragmentData = useFragment(HomeSectionLivancePayCardGroupFragment, data);
  const hasRecipient = Boolean(fragmentData.expensumRecipient?.recipient?.pagarmeId);

  if (!hasRecipient) {
    return <HomeSectionLivancePayEmptyState />;
  }

  const mapped = mapPaymentsToHomeData(fragmentData);

  const paymentsCount = `${mapped.pagos} / ${mapped.total}`;
  const totalPendingAmount = formatCurrencyFromCentsToReais(mapped.totalPendingAmount);
  const totalPaidAmount = formatCurrencyFromCentsToReais(mapped.totalPaidAmount);

  return (
    <>
      <CardGroupRoot>
        <Link
          to="/settings/payments/evolution-charts"
          id="livance-pay-card-payments-paid"
        >
          <CardGroupItem className="h-full">
            <CardGroupValue>{paymentsCount}</CardGroupValue>
            <CardGroupLabel>Pagamentos realizados</CardGroupLabel>
          </CardGroupItem>
        </Link>
        <Link
          to="/settings/payments/evolution-charts"
          id="livance-pay-card-payments-pending"
        >
          <CardGroupItem className="h-full">
            <CardGroupValue className="text-nowrap text-[clamp(1rem,5vw,1.5rem)] h-[40px]">
              {totalPendingAmount}
            </CardGroupValue>
            <CardGroupLabel>Valor total em aberto</CardGroupLabel>
          </CardGroupItem>
        </Link>
        <Link
          className="col-span-2 lg:col-span-1"
          to="/settings/payments/evolution-charts"
          id="livance-pay-card-payments-total"
        >
          <CardGroupItem fullWidth>
            <CardGroupValue>{totalPaidAmount}</CardGroupValue>
            <CardGroupLabel>Valor total pago</CardGroupLabel>
          </CardGroupItem>
        </Link>
      </CardGroupRoot>
      <p className="text-neutral-500 text-sm">{periodDetails}</p>
    </>
  );
};
